import { ThemeProvider } from '@fluentui/react';
import { IPScrollablePane, IPSeparator } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import CheckpointPreviewBar from 'components/Backups/CheckpointPreviewBar';
import CopilotDeepLink from 'components/Copilot/Panel/CopilotDeepLink';
import { DocumentJobHostProvider } from 'components/Copilot/Panel/Documents/DocumentJobHostProvider';
import ContentLoader from 'components/Loading/ContentLoader';
import SceneLoader from 'components/Loading/SceneLoader';
import NotificationSettingsDeepLink from 'components/NavBar/NotificationSettingsDeepLink';
import NavRoot from 'components/NavMain/NavRoot';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import ProtectedRoute from 'components/Routing/ProtectedRoute';
import { globalCloudSetupURL, globalNotFoundURL, globalToastDelay } from 'globalConstants';
import { globalPaddingScene } from 'globalStyles';
import posthogClient from 'posthog-js';
import { Fragment, Suspense, lazy, useContext, useEffect } from 'react';
import { Redirect, Route, BrowserRouter as Router, Switch, useHistory, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import AdminContentConfig from 'scenes/Admin/ContentConfig/AdminContentConfig';
import Dashboard from 'scenes/Dashboard/Dashboard';
import ISOControlReferenceRoute from 'scenes/Norms/ISOControlReferenceRoute';
import ProcessingActivitiesOverview from 'scenes/ProcessingActivities/ProcessingActivitiesOverview';
import ProcessingActivityDetails from 'scenes/ProcessingActivity/ProcessingActivityDetails';
import { TaskViewHubProvider } from 'scenes/Tasks/TaskViews/TaskViewHubProvider';
import { TaskViewStateProvider } from 'scenes/Tasks/TaskViews/TaskViewStateProvider';
import { FeatureTypes, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { darkTheme, lightTheme } from 'themes/runtime/globalThemes-v8';
import AppContentAreaTopLayerHost from './AppContentAreaTopLayerHost';
import AppContext from './AppContext';
import { ColorMode, DesignSystem, UnifiedThemeProvider } from './AppDesignSystem';
import AppRightSideBarLayerHost from './AppRightSideBarLayerHost';
import { getLegacyRedirects } from './AppRoutingRedirects';
import AppSceneLayerHost from './AppSceneLayerHost';

//
// Lazy load all scenes (React module splitting)
//
const TaskSchedule = lazy(() => import('scenes/Schedule/TaskSchedule'));
const Processes = lazy(() => import('scenes/Processes/Processes'));
const Objectives = lazy(() => import('scenes/Objectives/Objectives'));
const KPIs = lazy(() => import('scenes/KPIs/KPIs'));
const ProcessDetails = lazy(() => import('scenes/Process/ProcessDetails'));
const RisksOverview = lazy(() => import('scenes/Risks/RisksOverview'));
const RiskDetails = lazy(() => import('scenes/Risk/RiskDetails'));
const Controls = lazy(() => import('scenes/Controls/Controls'));
const ControlDetails = lazy(() => import('scenes/Control/ControlDetails'));
const Audits = lazy(() => import('scenes/Audits/Audits'));
const AuditDetails = lazy(() => import('scenes/Audit/AuditDetails'));
const ObjectiveDetails = lazy(() => import('scenes/Objective/ObjectiveDetails'));
const UpdateTenant = lazy(() => import('scenes/Enroll/UpdateTenant'));
const StartTenant = lazy(() => import('scenes/Enroll/StartTenant'));
const TasksOverview = lazy(() => import('scenes/Tasks/TasksOverview'));
const Themes = lazy(() => import('scenes/Themes/Themes'));
const ThemeDetails = lazy(() => import('scenes/Theme/ThemeDetails'));
const Norms = lazy(() => import('scenes/Norms/Norms'));
const Library = lazy(() => import('scenes/Library/Library'));
const LibraryEvidenceExport = lazy(() => import('../scenes/Library/Export/LibraryEvidenceExport'));
const StoreOverview = lazy(() => import('scenes/Store/StoreOverview'));
const AdminSubscription = lazy(() => import('scenes/Admin/Subscription/AdminSubscription'));
const CompanySettings = lazy(() => import('scenes/Admin/Company/CompanySettings'));
const AdminNorms = lazy(() => import('scenes/Admin/Norms/AdminNorms'));
const AdminGroups = lazy(() => import('scenes/Admin/Groups/AdminGroups'));
const AdminAuth = lazy(() => import('scenes/Admin/Auth/AdminAuth'));
const NotAuthorized = lazy(() => import('scenes/NotAuthorized/NotAuthorized'));
const NotFound = lazy(() => import('scenes/NotFound/NotFound'));
const Logout = lazy(() => import('scenes/Logout/Logout'));
const Welcome = lazy(() => import('scenes/Welcome/Welcome'));
const SetupPage = lazy(() => import('scenes/CloudSetup/SetupPage'));
const TenantAdminControls = lazy(() => import('scenes/TenantAdmin/Controls/TenantAdminControls'));
const TenantAdminGeneralKnowledge = lazy(
  () => import('scenes/TenantAdmin/GeneralKnowledge/TenantAdminGeneralKnowledge'),
);
const TenantAdminAITrace = lazy(() => import('scenes/TenantAdmin/AITrace/TenantAdminAITrace'));
const TenantAdminAICredits = lazy(() => import('scenes/TenantAdmin/AICredits/TenantAdminAICredits'));
const TenantAdminAIAuditTrace = lazy(() => import('scenes/TenantAdmin/AIAuditTrace/TenantAdminAIAuditTrace'));
const TenantAdminIdentityIssuers = lazy(() => import('scenes/TenantAdmin/IdentityIssuers/TenantAdminIdentityIssuers'));
const TenantAdminAIInstructions = lazy(() => import('scenes/TenantAdmin/AIInstructions/TenantAdminAIInstructions'));
const TenantAdminAISkills = lazy(() => import('scenes/TenantAdmin/AISkills/TenantAdminAISkills'));
const TenantAdminAIStandardInput = lazy(() => import('scenes/TenantAdmin/AIStandardInput/TenantAdminAIStandardInput'));
const TenantAdminAnnouncements = lazy(() => import('scenes/TenantAdmin/Announcements/TenantAdminAnnouncements'));
const AnnouncementEditScene = lazy(() => import('scenes/TenantAdmin/Announcements/AnnouncementEditScene'));
const TenantAdminManage = lazy(() => import('scenes/TenantAdmin/Manage/TenantAdminManage'));
const AdminOrgUnits = lazy(() => import('scenes/Admin/OrgUnits/AdminOrgUnits'));
const AdminReports = lazy(() => import('scenes/Admin/Reports/AdminReports'));
const AssetsOverview = lazy(() => import('scenes/Assets/AssetsOverview'));
const AssetDetails = lazy(() => import('scenes/Asset/AssetDetails'));
const PartnerAdminManage = lazy(() => import('scenes/PartnerAdmin/PartnerAdminCustomers'));
const PartnerDashboard = lazy(() => import('scenes/PartnerAdmin/PartnerDashboard'));
const KPIDetails = lazy(() => import('scenes/KPI/KPIDetails'));
const PartnerTenantDashboard = lazy(() => import('scenes/PartnerAdmin/PartnerTenantDashboard'));
const TenantAdminLogs = lazy(() => import('scenes/TenantAdmin/TenantLogs/TenantAdminLogs'));
const ProjectEntry = lazy(() => import('scenes/Project/ProjectEntry'));
const ProjectDetails = lazy(() => import('scenes/Project/ProjectDetails'));

//
// Lazy load components (React module splitting)
//
const CopilotPanel = lazy(() => import('components/Copilot/Panel/CopilotPanel'));
const NavBar = lazy(() => import('components/NavBar/NavBar'));
const MobileNavBar = lazy(() => import('components/NavBar/MobileNavBar'));

//
// Main app routing component
//
const AppRoutingContent = () => {
  const appContext = useContext(AppContext);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const isEvidenceExportRoute = pathname.startsWith('/library/export/');
  //the cloud setup page is for a cloud administrator without an ISOPlanner account: no app navigation, light only
  const isStandaloneRoute = isEvidenceExportRoute || pathname === globalCloudSetupURL;
  //Copilot is mounted above the route Switch, so on a customer dashboard its background polling and document
  //jobs would inherit the customer scope and address process ids that only exist in the partner tenant.
  //Copilot is not part of the impersonation surface, so do not render it there at all.
  const isPartnerCustomerRoute = pathname.startsWith('/partner/customer/');

  useEffect(() => {
    appContext.copilotBridge.closeIfUnsupported();
    posthogClient.capture('$pageview');
  }, [appContext.copilotBridge, pathname]);

  // Lets a Copilot action take the user to the page it belongs to. Registered here because this is
  // inside the router and the bridge itself cannot navigate. An action invoked on the page it already
  // belongs to navigates nowhere, so it does not leave a back-button entry behind.
  useEffect(() => {
    const subscription = appContext.copilotBridge.registerNavigationHandler((route) => {
      if (route === `${history.location.pathname}${history.location.hash}`) return;

      history.push(route);
    });

    return () => subscription.dispose();
  }, [appContext.copilotBridge, history]);

  const getTopNavbar = () => {
    if (!appContext.isMobileView) {
      return (
        <NavBar
          login={appContext.login}
          logout={appContext.logout}
          switchOrg={appContext.switchOrg}
          switchOrgUnit={appContext.switchOrgUnit}
        ></NavBar>
      );
    } else {
      return (
        <MobileNavBar
          login={appContext.login}
          logout={appContext.logout}
          switchOrg={appContext.switchOrg}
          switchOrgUnit={appContext.switchOrgUnit}
        ></MobileNavBar>
      );
    }
  };

  const getNavRoot = () => {
    if (!appContext.isMobileView) {
      return (
        <Fragment>
          <IPStack.Item>
            <NavRoot />
          </IPStack.Item>
          <IPSeparator vertical></IPSeparator>
        </Fragment>
      );
    }
  };

  const effectiveColorMode = isStandaloneRoute
    ? ColorMode.Light
    : appContext.useDarkMode
    ? ColorMode.Dark
    : ColorMode.Light;
  const effectiveTheme = isStandaloneRoute ? lightTheme : appContext.useDarkMode ? darkTheme : lightTheme;
  const renderPinnedCopilotPanel =
    appContext.copilotBridge.isOpen &&
    appContext.copilotBridge.isPinned &&
    !appContext.isMobileView &&
    !isPartnerCustomerRoute;

  return (
    <UnifiedThemeProvider mode={effectiveColorMode} system={DesignSystem.FluentV8}>
      {/* TODO: Remove this ThemeProvider when all components are migrated Fluent V9 */}
      <ThemeProvider applyTo="body" theme={effectiveTheme}>
        <IPStack
          className={isEvidenceExportRoute ? 'app-routing-evidence-export' : undefined}
          verticalFill
          styles={{ root: { height: '100vh' } }}
        >
          {/* Main App */}
          {!isStandaloneRoute && (
            <IPStack.Item>
              {/* Top bar */}
              {getTopNavbar()}
            </IPStack.Item>
          )}

          <IPStack.Item grow styles={{ root: { minHeight: 0, overflow: 'hidden' } }}>
            {/* Body */}
            <IPStack horizontal styles={{ root: { height: '100%', minHeight: 0, overflow: 'hidden' } }}>
              {/* Left side bar */}
              {!isStandaloneRoute && getNavRoot()}

              <IPStack.Item grow styles={{ root: { minHeight: 0, overflow: 'hidden' } }}>
                {/* Content area */}
                <IPStack styles={{ root: { height: '100%', minHeight: 0, overflow: 'hidden' } }}>
                  {/* Notification area */}
                  <IPStack.Item>
                    <AppContentAreaTopLayerHost />
                    <ContentLoader />
                    <CheckpointPreviewBar />
                    <ErrorMessage error={appContext.error} setError={appContext.setError} />
                    <ToastContainer position="top-right" autoClose={globalToastDelay} containerId="app" />
                    <ToastContainer position="bottom-center" autoClose={globalToastDelay} containerId="app-bottom" />
                  </IPStack.Item>

                  <IPStack.Item grow styles={{ root: { minHeight: 0, overflow: 'hidden', position: 'relative' } }}>
                    {/* Scene area */}
                    <AppSceneLayerHost />
                    <Suspense fallback={<SceneLoader />}>
                      <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
                        <Switch>
                          {/* 1. All users may navigate to unprotected routes, signed in or not
                                  2. When authentication is in progress, wait until completed by rendering the ProgressIndicator
                                  3. When the user is not authenticated and requests a protected route, navigate to not-authorized
                                  4. When the user is authenticated, navigate to the protected route, the route itself is responsible for checking role and redirect to not-authorized
                                  5. Navigate to not-found when the route is not found
                              */}

                          {/* These routes are not protected  */}
                          <Route path="/logout" render={(props) => <Logout {...props} />} />
                          <Route path={globalNotFoundURL} render={(props) => <NotFound {...props} />} />
                          <Route path="/not-authorized" render={(props) => <NotAuthorized {...props} />} />
                          <Route path="/reload" render={() => <SceneLoader />} />
                          <Route path={globalCloudSetupURL} exact render={() => <SetupPage />} />

                          {/* Route all paths (not exact /) to the SceneLoader component when authorization is in progress */}
                          {appContext.isAuthInProgress && <Route path="/" render={() => <SceneLoader />} />}

                          {/* Via this route, a new trial can be started for the currently unknown tenant */}
                          <Route
                            path="/start"
                            exact
                            render={(props) => {
                              if (appContext.isAuthInProgress) {
                                return <SceneLoader />;
                              } else if (
                                appContext.isAuthenticated &&
                                !appContext.user.login.tenantId &&
                                !appContext.user.login.tenantLicensed
                              ) {
                                return <StartTenant {...props} />; //start trial
                              } else if (appContext.user.login.userLicensed) {
                                return <UpdateTenant {...props} />; //update lead source and/or packages
                              } else if (
                                appContext.user.login.tenantId &&
                                appContext.user.login.tenantLicensed === false &&
                                hasUserFeatureGenericManager(appContext)
                              ) {
                                //redirect to dashboard when a registered tenant has no
                                //license anymore (trial/subscription ended) and the user has roles
                                return <Redirect to="/dashboard"></Redirect>;
                              } else {
                                //1. not authenticated
                                //2. tenant is suspended
                                //3. user has no license
                                return <Welcome />;
                              }
                            }}
                          />

                          {/* Route to home and switch between is/not authenticated */}
                          <Route
                            path="/"
                            exact
                            render={(props) =>
                              /* Show dashboard when user has a license or when a registered tenant has no 
                                  license anymore (trial/subscription ended) and the user has roles */
                              appContext.user.login.userLicensed ||
                              (appContext.user.login.tenantId &&
                                appContext.user.login.tenantLicensed === false &&
                                hasUserFeatureGenericManager(appContext)) ? (
                                <Redirect to="/dashboard"></Redirect>
                              ) : (
                                <Welcome />
                              )
                            }
                          />

                          {/* After this point all routes are protected and must have an authenticated user */}
                          {!appContext.isAuthenticated && <Redirect to="/"></Redirect>}

                          {/* All authenticated users */}
                          <Route
                            path="/references/isocontrol/:isoControlId"
                            exact
                            component={ISOControlReferenceRoute}
                          />
                          <Route path="/copilot" render={(props) => <CopilotDeepLink {...props} />} />
                          <Route
                            path="/settings/notifications"
                            render={(props) => <NotificationSettingsDeepLink {...props} />}
                          />
                          <Route path="/dashboard" render={(props) => <Dashboard {...props} />} />

                          {/* The nav entry resolves the project to open; `exact`, so `/project/{id}` still reaches ProjectDetails. */}
                          <ProtectedRoute
                            exact
                            featureType={FeatureTypes.Projects}
                            path="/project"
                            render={(props) => <ProjectEntry {...props} />}
                          />
                          {/* One route for the whole project scene, so the rail and the guide panel never remount between segments. */}
                          <ProtectedRoute
                            featureType={FeatureTypes.Projects}
                            path="/project/:urlProjectParam"
                            render={(props) => <ProjectDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Requirements}
                            path="/themes"
                            render={(props) => <Themes {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Requirements}
                            path="/theme/:urlThemeParam"
                            render={(props) => <ThemeDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Assets}
                            path="/assets"
                            render={(props) => <AssetsOverview {...props}></AssetsOverview>}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Assets}
                            path="/asset/:urlAssetParam"
                            render={(props) => <AssetDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Risks}
                            path="/risks"
                            render={(props) => <RisksOverview {...props}></RisksOverview>}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Risks}
                            path="/risk/:urlRiskParam"
                            render={(props) => <RiskDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Controls}
                            path="/controls"
                            render={(props) => <Controls {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Controls}
                            path="/control/:urlControlParam"
                            render={(props) => <ControlDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Audit}
                            path="/audits"
                            render={(props) => <Audits {...props} />}
                          />
                          {/* The execute tab is the one audit surface a team member may open: the
                              invitation mail sends them here. It must stay ABOVE the route below,
                              which is matched non-exactly - that ordering is what keeps the details
                              and reporting tabs manager-only without any in-scene guard. */}
                          <ProtectedRoute
                            featureType={FeatureTypes.AuditParticipant}
                            path="/audit/:urlAuditParam/execute"
                            render={(props) => <AuditDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Audit}
                            path="/audit/:urlAuditParam"
                            render={(props) => <AuditDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Processes}
                            path="/processes"
                            render={(props) => <Processes {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Objectives}
                            path="/objectives"
                            render={(props) => <Objectives {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.KPIsAndForms}
                            path="/kpis"
                            render={(props) => <KPIs {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Processes}
                            path="/process/:urlProcessParam"
                            render={(props) => <ProcessDetails {...props} />}
                          />

                          <ProtectedRoute
                            featureType={FeatureTypes.Objectives}
                            path="/objective/:urlObjectiveParam"
                            render={(props) => <ObjectiveDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.KPIsAndForms}
                            path="/kpi/:urlKPIParam"
                            render={(props) => <KPIDetails {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Standards}
                            path="/standards"
                            render={(props) => <Norms {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Tasks}
                            path="/tasks"
                            render={(props) => <TasksOverview {...props} />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Library}
                            path="/library/export/:documentId"
                            render={() => <LibraryEvidenceExport />}
                          />
                          <ProtectedRoute
                            featureType={FeatureTypes.Library}
                            path="/library"
                            render={(props) => <Library {...props} />}
                          />

                          <ProtectedRoute
                            featureType={FeatureTypes.ProcessingActivities}
                            path="/processingactivities"
                            render={(props) => <ProcessingActivitiesOverview {...props} />}
                          />

                          <ProtectedRoute
                            featureType={FeatureTypes.ProcessingActivities}
                            path="/processingactivity/:urlProcessingActivityParam"
                            render={(props) => <ProcessingActivityDetails {...props} />}
                          />

                          <ProtectedRoute
                            path="/schedule"
                            featureType={FeatureTypes.AnnualPlan}
                            render={(props) => <TaskSchedule {...props} />}
                          />

                          {/* Manager */}
                          <ProtectedRoute
                            path="/admin/standards"
                            featureType={FeatureTypes.GenericManager}
                            render={(props) => <AdminNorms {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/settings"
                            featureType={FeatureTypes.GenericManager}
                            render={(props) => <CompanySettings {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/store"
                            featureType={FeatureTypes.GenericManager}
                            render={(props) => <StoreOverview {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/groups"
                            featureType={FeatureTypes.GenericManager}
                            render={(props) => <AdminGroups {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/reports"
                            featureType={FeatureTypes.GenericManager}
                            render={(props) => <AdminReports {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/content"
                            featureType={FeatureTypes.GenericManager}
                            render={(props) => <AdminContentConfig {...props} />}
                          />

                          {/* Admin */}

                          <ProtectedRoute
                            path="/admin/users"
                            featureType={FeatureTypes.AdminAuth}
                            render={(props) => <AdminAuth {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/subscription"
                            featureType={FeatureTypes.AdminSubscription}
                            render={(props) => <AdminSubscription {...props} />}
                          />

                          <ProtectedRoute
                            path="/admin/orgunits"
                            featureType={FeatureTypes.AdminOrgUnits}
                            render={(props) => <AdminOrgUnits {...props} />}
                          />

                          {/* Partner Admin */}
                          <ProtectedRoute
                            path="/partner/customers"
                            featureType={FeatureTypes.PartnerAdmin}
                            render={(props) => <PartnerAdminManage {...props} />}
                          />

                          <ProtectedRoute
                            path="/partner/dashboard"
                            featureType={FeatureTypes.PartnerAdmin}
                            render={(props) => <PartnerDashboard {...props} />}
                          />

                          <ProtectedRoute
                            path="/partner/customer/:urlTenantParam"
                            featureType={FeatureTypes.PartnerAdmin}
                            render={(props) => <PartnerTenantDashboard {...props} />}
                          />

                          {/* Tenant Admin */}
                          <ProtectedRoute
                            path="/tenant/manage"
                            featureType={FeatureTypes.TenantAdmin}
                            render={(props) => <TenantAdminManage {...props} />}
                          />

                          <ProtectedRoute
                            path="/tenant/controls"
                            featureType={FeatureTypes.TenantISOAdmin}
                            render={(props) => <TenantAdminControls {...props} />}
                          />

                          <ProtectedRoute
                            path="/tenant/logs"
                            featureType={FeatureTypes.TenantAdmin}
                            render={(props) => <TenantAdminLogs {...props} />}
                          />

                          <ProtectedRoute
                            path="/tenant/ai-trace"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAITrace />}
                          />

                          <ProtectedRoute
                            path="/tenant/ai-audit-trace"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAIAuditTrace />}
                          />

                          <ProtectedRoute
                            path="/tenant/ai-credits"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAICredits />}
                          />

                          <ProtectedRoute
                            path="/tenant/identity-issuers"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminIdentityIssuers />}
                          />

                          <ProtectedRoute
                            path="/tenant/ai-instructions"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAIInstructions />}
                          />

                          <ProtectedRoute
                            path="/tenant/ai-skills"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAISkills />}
                          />

                          <ProtectedRoute
                            path="/tenant/documents"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminGeneralKnowledge />}
                          />

                          <ProtectedRoute
                            path="/tenant/ai-standard-input"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAIStandardInput />}
                          />

                          <ProtectedRoute
                            path="/tenant/announcements/:announcementId"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <AnnouncementEditScene />}
                          />

                          <ProtectedRoute
                            path="/tenant/announcements"
                            featureType={FeatureTypes.TenantAdmin}
                            render={() => <TenantAdminAnnouncements />}
                          />

                          {/* Paths that other places still hold, each pointing at the route that
                              answers for it. They come after the routes, so a live route wins. */}
                          {getLegacyRedirects(search)}

                          <Redirect to="/not-found"></Redirect>
                        </Switch>
                      </IPScrollablePane>
                    </Suspense>
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
              {/* Copilot panel area. Left out entirely on a customer dashboard rather than rendered empty:
                  copilot is not part of the impersonation surface, and its background polling and document jobs
                  would address process ids that only exist in the partner's own tenant. */}
              {!isPartnerCustomerRoute && (
                <IPStack.Item
                  styles={{
                    root: {
                      display: 'flex',
                      height: '100%',
                      marginLeft: renderPinnedCopilotPanel ? globalPaddingScene : 0,
                      minHeight: 0,
                      overflow: 'hidden',
                    },
                  }}
                >
                  <Suspense fallback={<SceneLoader />}>
                    <CopilotPanel />
                  </Suspense>
                </IPStack.Item>
              )}
              <IPStack.Item>
                {/* Panel area */}
                <AppRightSideBarLayerHost />
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </ThemeProvider>
    </UnifiedThemeProvider>
  );
};

const AppRouting = () => {
  return (
    <Router>
      {/* TaskView state + the SignalR hub are app-wide: the sidebar (NavMain) and
          the Tasks scene share one in-memory source of truth and live cross-tab sync. */}
      <TaskViewHubProvider>
        <TaskViewStateProvider>
          <DocumentJobHostProvider>
            <AppRoutingContent />
          </DocumentJobHostProvider>
        </TaskViewStateProvider>
      </TaskViewHubProvider>
    </Router>
  );
};

export default AppRouting;
